import { Component, EventEmitter, Output, Input, OnDestroy, OnChanges, SimpleChanges, ViewChild, AfterViewInit, ElementRef, HostBinding, HostListener } from '@angular/core';

import { NgTemplateOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { DEFAULT_MAP_TOOLBAR_STATE, MapToolbarState, SwipePresetId } from '../../../../../../shared/types/map.types';

interface Basemap {
  id: string;
  type: 'imagery' | 'vector';
  title: string;
  url?: string;
  layerType: string;
  format?: string;
  isDefault?: boolean;
  compressionQuality?: number;
  thumbnail?: string;
  portalItem?: {
    id: string;
  };
  displayOrder?: number;
}

interface TypeFilter {
  id: string;
  label: string;
  icon: string;
}

@Component({
    selector: 'app-map-controls',
    imports: [
    NgTemplateOutlet,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTabsModule
],
    templateUrl: './map-controls.component.html',
    styleUrls: ['./map-controls.component.css']
})
export class MapControlsComponent implements OnDestroy, OnChanges, AfterViewInit {
  @ViewChild('menuTriggerRef') menuTrigger!: MatMenuTrigger;
  @ViewChild('layersPopup') layersPopupRef!: ElementRef;
  @ViewChild('layersToggleButton') layersToggleButtonRef!: ElementRef;

  @Input() mapScreenshotUrl?: string;
  @Input() organizationBasemaps: Basemap[] = [];
  @Input() projectLayers: MapToolbarState = { ...DEFAULT_MAP_TOOLBAR_STATE };
  @Input() canManageLayers = false;
  /** True when the host shows design layers in the View panel; this control then offers only the basemap. */
  @Input() projectLayersInSidenav = false;
  @Output() projectLayerToggle = new EventEmitter<string>();
  @Output() projectLayerOpacity = new EventEmitter<{ id: string; opacity: number }>();
  @Output() projectLayerLabels = new EventEmitter<string>();
  @Output() projectLayerZoom = new EventEmitter<string>();
  @Output() projectLayersReset = new EventEmitter<void>();
  @Output() projectLayersRetry = new EventEmitter<void>();
  @Output() addProjectLayer = new EventEmitter<void>();
  @Output() projectLayerSwipe = new EventEmitter<SwipePresetId>();
  /**
   * Inside a box instead of the window (the design-context Location picker's map): the stack and its popup are
   * positioned against the nearest positioned ancestor, and the swipe comparison (a main-map tool) is left out.
   */
  @Input() @HostBinding('class.map-controls-contained') contained = false;
  /** The host's current basemap id (a restored per-project choice); without it the control starts on Default. */
  @Input() set basemapId(id: string | null | undefined) { if (id) this.currentBasemap = id; }
  layerSearch = '';

  get filteredProjectLayers() {
    const query = this.layerSearch.trim().toLocaleLowerCase();
    // Published rows already follow manifest group/layer order. Legacy-only
    // lists retain their historical last-added-on-top ordering.
    const entries = [...this.projectLayers.overlayLayers];
    if (!this.hasPublishedGroups) entries.reverse();
    return entries.filter(entry => entry.name.toLocaleLowerCase().includes(query));
  }

  get filteredProjectLayerGroups(): { name: string; entries: MapToolbarState['overlayLayers'] }[] {
    const groups = new Map<string, MapToolbarState['overlayLayers']>();
    for (const entry of this.filteredProjectLayers) {
      const name = entry.group || 'Project layers';
      const current = groups.get(name) || [];
      current.push(entry);
      groups.set(name, current);
    }
    return Array.from(groups, ([name, entries]) => ({ name, entries }));
  }

  get hasPublishedGroups(): boolean {
    return this.projectLayers.overlayLayers.some(entry => !!entry.group);
  }

  setLayerSearch(event: Event): void {
    this.layerSearch = (event.target as HTMLInputElement).value;
  }

  changeLayerOpacity(id: string, event: Event): void {
    this.projectLayerOpacity.emit({ id, opacity: Number((event.target as HTMLInputElement).value) / 100 });
  }

  opacityPercent(opacity: number | undefined): number { return Math.round((opacity ?? 1) * 100); }

  closeLayers(): void {
    this.showPopup = false;
    this.layersToggleButtonRef?.nativeElement?.focus();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { if (this.showPopup) this.closeLayers(); }

  // Layer inputs/outputs
  @Input() selectedLayers: { siteBoundary: boolean; picture: boolean; asBuilt: boolean } = {
    siteBoundary: true,
    picture: false,
    asBuilt: true
  };

  // Type filter inputs/outputs
  // Base types (bid item / delivery / activities). Control forms render as one
  // dynamic toggle PER GROUP (see availableFormGroups), mirroring map-sidenav.
  private readonly FORM_GROUP_PREFIX = 'form_group:';

  baseTypeFilters: TypeFilter[] = [
    { id: 'bidItem', label: 'Bid item', icon: 'type-bid-item' },
    { id: 'delivery', label: 'Deliveries', icon: 'type-delivery' },
    { id: 'activities', label: 'Activities', icon: 'type-activities' }
  ];

  // Single flat Forms toggle — single-day / fallback when no groups are published.
  private readonly flatFormsTypeFilter: TypeFilter = { id: 'forms', label: 'Forms', icon: 'type-quality' };

  // Group toggles default UNCHECKED like the base types; all use the standard
  // grey 'type-quality' glyph (the colored marker icon is only for the map pin).
  @Input() set availableFormGroups(groups: Array<{ id: string; label: string; icon: string | null }>) {
    this._availableFormGroups = (groups || []).map(g => ({
      id: `${this.FORM_GROUP_PREFIX}${g.id}`,
      label: g.label,
      icon: 'type-quality',
    }));
  }
  get availableFormGroups() {
    return this._availableFormGroups as any;
  }
  private _availableFormGroups: TypeFilter[] = [];

  // Combined list rendered in the popup: base types + per-group toggles (or the
  // flat Forms toggle when no groups are published).
  get typeFilters(): TypeFilter[] {
    if (this._availableFormGroups.length > 0) {
      return [...this.baseTypeFilters, ...this._availableFormGroups];
    }
    return [...this.baseTypeFilters, this.flatFormsTypeFilter];
  }

  @Input() selectedTypeFilters: string[] = [];

  @Output() zoomIn = new EventEmitter<void>();
  @Output() zoomOut = new EventEmitter<void>();
  @Output() basemapChanged = new EventEmitter<any>();
  @Output() layerChange = new EventEmitter<{ siteBoundary: boolean; picture: boolean; asBuilt: boolean }>();
  @Output() typeFilterChange = new EventEmitter<string[]>();

  currentBasemap: string = 'default';
  showTooltip = false;
  fadeOutTooltip = false;
  showPopup = false;
  private tooltipTimeout: any;
  sortedBasemaps: Basemap[] = [];

  constructor(private elementRef: ElementRef) {}

  /**
   * Close popup when clicking outside
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.showPopup) return;

    const target = event.target as HTMLElement;

    // Check if click is inside popup or toggle button
    const clickedInsidePopup = this.layersPopupRef?.nativeElement?.contains(target);
    const clickedToggleButton = this.layersToggleButtonRef?.nativeElement?.contains(target);

    if (!clickedInsidePopup && !clickedToggleButton) {
      this.showPopup = false;
    }
  }

  ngOnInit(): void {
    this.sortBasemaps();
    // currentBasemap starts on 'default' unless the host passed basemapId (a saved choice).
  }

  ngAfterViewInit(): void {
    // Subscribe to menu open events to add class
    if (this.menuTrigger) {
      this.menuTrigger.menuOpened.subscribe(() => {
        this.addBasemapCountClass();
      });
    }
  }

  private addBasemapCountClass(): void {
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      const menuPanel = document.querySelector('.mat-mdc-menu-panel.basemaps-menu') as HTMLElement;
      if (menuPanel) {
        // Remove any existing basemap count classes
        menuPanel.classList.remove('basemap-count-2', 'basemap-count-3', 'basemap-count-many');

        // Add appropriate class based on basemap count
        const basemapCount = this.sortedBasemaps.length;
        if (basemapCount === 2) {
          menuPanel.classList.add('basemap-count-2');
          menuPanel.setAttribute('data-basemap-count', '2');
        } else if (basemapCount === 3) {
          menuPanel.classList.add('basemap-count-3');
          menuPanel.setAttribute('data-basemap-count', '3');
        } else {
          menuPanel.classList.add('basemap-count-many');
          menuPanel.setAttribute('data-basemap-count', 'many');
        }
      }
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-sort basemaps when organizationBasemaps input changes
    if (changes['organizationBasemaps']) {
      this.sortBasemaps();
    }
  }

  private sortBasemaps(): void {
    // Create a list of all basemaps
    const allBasemaps: Basemap[] = [];

    // 1. Always add Default map first (with hardcoded portal ID)
    allBasemaps.push({
      id: 'default',
      type: 'vector',
      title: 'Default',
      layerType: 'vector',
      thumbnail: 'assets/icons/map-thumbnails/default-map-thumbnail.png',
      portalItem: {
        id: '9bc1cb8a67f4422c882bc55a4d4792ef' // Use the same portal ID as esri-map component
      }
    });

    // 2. Add all organization basemaps sorted by displayOrder
    const orgBasemaps = [...this.organizationBasemaps] // Create a copy to avoid mutating the input
      .sort((a, b) => {
        // Sort by displayOrder if available
        const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      })
      .map(b => ({
        ...b,
        id: b.id || `org-basemap-${Math.random().toString(36).substring(7)}`
      }));

    allBasemaps.push(...orgBasemaps);

    // 3. Always add Satellite map last
    allBasemaps.push({
      id: 'satellite',
      type: 'imagery',
      title: 'Satellite',
      layerType: 'imagery',
      thumbnail: 'assets/icons/map-thumbnails/satellite-map-thumbnail.png'
    });

    this.sortedBasemaps = allBasemaps;
  }

  onZoomIn(): void {
    this.zoomIn.emit();
  }

  onZoomOut(): void {
    this.zoomOut.emit();
  }

  selectBasemap(basemapId: string): void {
    this.currentBasemap = basemapId;
    // Find the basemap and emit the full object
    const basemap = this.sortedBasemaps.find(b => b.id === basemapId);
    if (basemap) {
      this.basemapChanged.emit(basemap);
    }
  }

  getBasemapTitle(basemapId: string): string {
    const basemap = this.sortedBasemaps.find(b => b.id === basemapId);
    return basemap ? basemap.title : 'Map';
  }

  /**
   * Get the thumbnail for a basemap
   */
  getBasemapThumbnail(basemapId: string): string {
    const basemap = this.sortedBasemaps.find(b => b.id === basemapId);

    // For basemaps with thumbnails, use them
    if (basemap?.thumbnail) {
      return basemap.thumbnail;
    }

    // Default thumbnails based on type and ID
    if (basemap?.id === 'default') {
      // Default vector basemap
      return 'assets/icons/map-thumbnails/default-map-thumbnail.png';
    } else if (basemap?.id === 'satellite') {
      // Default satellite basemap (when no org imagery exists)
      return 'assets/icons/map-thumbnails/satellite-map-thumbnail.png';
    } else {
      // Organization basemaps without thumbnail
      return 'assets/icons/map-thumbnails/org-basemap-default-thumbnail.png';
    }
  }

  /**
   * Get the current basemap for tooltip
   */
  getCurrentBasemapTitle(): string {
    return this.getBasemapTitle(this.currentBasemap);
  }

  onMouseEnter(): void {
    this.showTooltip = true;
    this.fadeOutTooltip = false;

    // Clear any existing timeout
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
  }

  onMouseLeave(): void {
    // Start fade out after 3 seconds
    this.tooltipTimeout = setTimeout(() => {
      this.fadeOutTooltip = true;

      // Hide completely after fade animation
      setTimeout(() => {
        this.showTooltip = false;
        this.fadeOutTooltip = false;
      }, 300); // Match CSS animation duration
    }, 3000);
  }

  ngOnDestroy(): void {
    if (this.tooltipTimeout) {
      clearTimeout(this.tooltipTimeout);
    }
  }

  /**
   * Toggle the layers/settings popup
   */
  togglePopup(): void {
    this.showPopup = !this.showPopup;
  }

  /**
   * Handle layer toggle (Site geometry, As-builts, Picture)
   */
  onLayerToggle(layerKey: 'siteBoundary' | 'picture' | 'asBuilt'): void {
    this.selectedLayers = {
      ...this.selectedLayers,
      [layerKey]: !this.selectedLayers[layerKey]
    };
    this.layerChange.emit(this.selectedLayers);
  }

  /**
   * Toggle type filter (Bid item, Deliveries, Activities, form groups).
   * Form group toggles behave like base types: present = shown, absent = hidden.
   */
  toggleTypeFilter(typeId: string): void {
    const index = this.selectedTypeFilters.indexOf(typeId);
    if (index > -1) {
      this.selectedTypeFilters = this.selectedTypeFilters.filter(id => id !== typeId);
    } else {
      this.selectedTypeFilters = [...this.selectedTypeFilters, typeId];
    }
    this.typeFilterChange.emit([...this.selectedTypeFilters]);
  }

  /**
   * Check if a type filter is selected
   */
  isTypeFilterChecked(typeId: string): boolean {
    return this.selectedTypeFilters.includes(typeId);
  }
}
